import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import LanguageToggle from './LanguageToggle';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mode, setMode] = useState('signin');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState('');

  const ALLOWED_DOMAINS = ['@kunlene-sz.com', '@kunlene-yn.com'];

  async function handleSignIn(e) {
    e.preventDefault();
    setError(''); setInfo('');

    if (!ALLOWED_DOMAINS.some(d => email.toLowerCase().endsWith(d))) {
      setError(t('login.err_domain'));
      return;
    }

    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });
      if (signInError) {
        if (signInError.message.includes('Invalid login')) {
          setError(t('login.err_invalid_creds'));
        } else if (signInError.message.includes('Email not confirmed')) {
          setError(t('login.err_not_confirmed'));
        } else {
          setError(signInError.message);
        }
      }
    } catch (e) {
      setError(t('login.err_connection'));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError(''); setInfo('');

    if (!ALLOWED_DOMAINS.some(d => email.toLowerCase().endsWith(d))) {
      setError(t('login.err_domain'));
      return;
    }

    setLoading(true);
    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(
        email.toLowerCase().trim(),
        { redirectTo: window.location.origin }
      );
      if (resetError) {
        setError(resetError.message);
      } else {
        setInfo(t('login.msg_reset_sent'));
      }
    } catch (e) {
      setError(t('login.err_connection'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg)',
      padding: 20,
    }}>
      <div style={{
        width: '100%',
        maxWidth: 420,
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-xl)',
        padding: '36px 32px',
        boxShadow: 'var(--shadow2)',
      }}>
        {/* Language toggle — top right of card */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <LanguageToggle />
        </div>

        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28 }}>
          <div style={{
            width: 36, height: 36, background: 'var(--blue)',
            borderRadius: 9, display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: 13, fontWeight: 800, color: 'white',
            letterSpacing: '-.03em',
          }}>PP</div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-.04em', color: 'var(--t0)' }}>
              MAP <span style={{ color: 'var(--blue)' }}>v5</span>
            </div>
            <div style={{ fontSize: 11, color: 'var(--t2)', marginTop: 1 }}>
              {t('login.tagline')}
            </div>
          </div>
        </div>

        <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--t0)', marginBottom: 6, letterSpacing: '-.02em' }}>
          {mode === 'signin' ? t('login.title_signin') : t('login.title_forgot')}
        </h2>
        <p style={{ fontSize: 12, color: 'var(--t2)', marginBottom: 24, lineHeight: 1.6 }}>
          {mode === 'signin' ? t('login.subtitle_signin') : t('login.subtitle_forgot')}
        </p>

        <form onSubmit={mode === 'signin' ? handleSignIn : handleForgot}>
          <div className="form-group">
            <div className="form-label">
              <span className="form-label-text">{t('login.label_email')}</span>
            </div>
            <input
              type="email"
              className="form-input"
              placeholder="yourname@kunlene-sz.com / @kunlene-yn.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              required
              disabled={loading}
            />
          </div>

          {mode === 'signin' && (
            <div className="form-group">
              <div className="form-label">
                <span className="form-label-text">{t('login.label_password')}</span>
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setError(''); setInfo(''); }}
                  style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 11, cursor: 'pointer', fontFamily: 'var(--f)' }}
                >
                  {t('login.btn_forgot')}
                </button>
              </div>
              <input
                type="password"
                className="form-input"
                placeholder={t('login.placeholder_password')}
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                disabled={loading}
              />
            </div>
          )}

          {error && (
            <div style={{
              background: 'var(--red-l)',
              border: '1px solid var(--red-b)',
              borderRadius: 'var(--radius)',
              padding: '9px 13px',
              fontSize: 12,
              color: '#fca5a5',
              marginBottom: 14,
              lineHeight: 1.5,
            }}>
              {error}
            </div>
          )}

          {info && (
            <div style={{
              background: 'var(--green-l)',
              border: '1px solid var(--green-b)',
              borderRadius: 'var(--radius)',
              padding: '9px 13px',
              fontSize: 12,
              color: '#6ee7b7',
              marginBottom: 14,
              lineHeight: 1.5,
            }}>
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary btn-lg"
            style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}
          >
            {loading ? t('login.btn_signin_loading') : mode === 'signin' ? t('login.btn_signin') : t('login.btn_send_reset')}
          </button>

          {mode === 'forgot' && (
            <button
              type="button"
              onClick={() => { setMode('signin'); setError(''); setInfo(''); }}
              style={{
                width: '100%', background: 'none', border: 'none',
                color: 'var(--t2)', fontSize: 12, cursor: 'pointer',
                fontFamily: 'var(--f)', padding: '6px 0',
              }}
            >
              {t('login.btn_back_signin')}
            </button>
          )}
        </form>

        <div style={{
          marginTop: 24,
          paddingTop: 18,
          borderTop: '1px solid var(--border)',
          fontSize: 11,
          color: 'var(--t3)',
          lineHeight: 1.6,
          textAlign: 'center',
        }}>
          {t('login.footer_secure')}<br/>
          {t('login.footer_admin')}
        </div>
      </div>
    </div>
  );
}
